import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const HowPage = () => {
  return (
    <Layout>
      {/* Hero - How We Do It */}
      <section className="pt-24 pb-12 lg:pt-32 lg:pb-20 bg-black">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal className="mx-auto mb-8" trigger="onLoad">
              <p className="label bg-purple text-white px-3 py-1 inline-block">HOW WE DO IT</p>
            </Reveal>
            <Reveal className="mx-auto mb-8 pb-4 !overflow-visible" trigger="onLoad" delay={0.1}>
              <h1 className="display-md text-white">
                We don't start with <span className="relative inline-block pb-1 text-[#c0ff09]">
                  aesthetics.
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-2 text-[#c0ff09]"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2,8 Q50,0 98,8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h1>
            </Reveal>
            <div className="text-left max-w-3xl mx-auto space-y-4">
              <Reveal trigger="onLoad" delay={0.2}>
                <p className="text-sm md:text-base text-white/60 leading-relaxed font-light">
                  We start with why your brand exists, who it serves, and how it should grow.
                </p>
              </Reveal>
              <Reveal trigger="onLoad" delay={0.3}>
                <p className="text-sm md:text-base text-white/60 leading-relaxed font-light">
                  Our integrated, strategy-led approach combines brand strategy, marketing, performance, and visual identity into one coherent system, balancing long-term brand building with short-term growth.
                </p>
              </Reveal>
              <Reveal trigger="onLoad" delay={0.4}>
                <p className="text-sm md:text-base text-white leading-relaxed font-bold">
                  Not disconnected activities.<br />
                  One direction.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* The Long and the Short of It */}
      <section className="section-padding bg-black border-t border-white/10">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <Reveal className="mb-8">
              <h2 className="text-3xl font-bold text-white">The Long and the Short of It</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-12 gap-3 md:gap-12 mb-8 items-center">
              {/* Les Binet - Left */}
              <div className="lg:col-span-4">
                <Reveal trigger="onLoad">
                  <div className="bg-black border border-white/10 rounded-lg overflow-hidden transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src="/images/thought-leaders/les_binet.jpg" alt="Les Binet" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="p-2 md:p-4 text-center bg-purple">
                      <p className="text-white text-sm md:text-base font-bold leading-tight">Les Binet</p>
                      <p className="text-[10px] md:text-xs text-white/80 leading-tight">Marketing Effectiveness Researcher</p>
                    </div>
                  </div>
                </Reveal>
              </div>

              {/* Text Content - Center */}
              <div className="col-span-2 lg:col-span-4 order-3 lg:order-none text-center">
                <Reveal delay={0.1}>
                  <p className="text-lg text-white/60 font-light mb-2">
                    Our thinking is strongly influenced by <span className="text-white font-bold">The Long and the Short of It</span> by Les Binet and Peter Field.
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-lg text-white font-bold mb-2">Their research makes one thing clear:</p>
                </Reveal>
                <ul className="space-y-1 mb-4 inline-block text-left">
                  {[
                    "Long-term brand building creates future demand.",
                    "Short-term activation captures present demand.",
                    "Focusing only on performance delivers short spikes but weak brands.",
                    "Focusing only on branding builds fame without momentum."
                  ].map((text, i) => (
                    <Reveal key={i} delay={0.3 + (i * 0.1)}>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-purple rounded-full mt-2"></div>
                        <p className="text-white/60">{text}</p>
                      </li>
                    </Reveal>
                  ))}
                </ul>
                <Reveal delay={0.7}>
                  <div className="flex justify-center">
                    <p className="text-base max-w-2xl">
                      <span className="bg-white text-purple px-4 py-2 rounded inline-block font-bold">
                        We design systems where both work together, especially important in price-sensitive, high-choice markets like India.
                      </span>
                    </p>
                  </div>
                </Reveal>
              </div>

              {/* Peter Field - Right */}
              <div className="lg:col-span-4">
                <Reveal delay={0.2} trigger="onLoad">
                  <div className="bg-black border border-white/10 rounded-lg overflow-hidden transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src="/images/thought-leaders/peter_field.jpg" alt="Peter Field" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="p-2 md:p-4 text-center bg-purple">
                      <p className="text-white text-sm md:text-base font-bold leading-tight">Peter Field</p>
                      <p className="text-[10px] md:text-xs text-white/80 leading-tight">Marketing Effectiveness Researcher</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Balancing Brand and Performance */}
      <section className="section-padding bg-black border-t border-white/10">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <Reveal className="mb-8">
              <h2 className="text-3xl font-bold text-white">Balancing Brand and Performance</h2>
            </Reveal>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-4">
                <Reveal trigger="onLoad">
                  <div className="bg-black border border-white/10 rounded-lg overflow-hidden transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src="/images/thought-leaders/mark_ritson.png" alt="Mark Ritson" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="p-4 text-center bg-purple">
                      <p className="text-white font-bold">Mark Ritson</p>
                      <p className="text-xs text-white/80">Marketing Professor & Brand Consultant</p>
                    </div>
                  </div>
                </Reveal>
              </div>
              <div className="lg:col-span-8">
                <Reveal delay={0.1}>
                  <p className="text-lg text-white/60 font-light mb-2">
                    This balance is further reinforced by <span className="text-white font-bold">Mark Ritson</span>.
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-white/60 mb-2">
                    On one side, he argues for consistent, emotional, multi-year brand building.
                  </p>
                </Reveal>
                <Reveal delay={0.3}>
                  <p className="text-white/60 mb-4">
                    On the other, targeted, agile, performance-led marketing, not as a numbers obsession, but as a learning engine.
                  </p>
                </Reveal>
                <Reveal delay={0.4}>
                  <p className="text-lg text-white font-bold mb-2">
                    What connects these two extremes are distinctive brand assets:
                  </p>
                </Reveal>
                <Reveal delay={0.5}>
                  <p className="text-white/60 mb-4">
                    clear positioning, memory structures, symbols, codes, and consistency that ensure every short-term action strengthens the long term.
                  </p>
                </Reveal>
                <Reveal delay={0.6}>
                  <p className="text-base">
                    <span className="bg-white text-purple px-3 py-1.5 rounded inline-block font-bold">
                      This is where many brands break. This is where we focus.
                    </span>
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nueva Media Solutions Strategic Framework */}
      <section id="framework" className="section-padding bg-white border-t border-black/5">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <Reveal className="mx-auto mb-8">
                <p className="label bg-purple text-white px-3 py-1 inline-block">Nueva Media Solutions Strategic Framework</p>
              </Reveal>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="flex flex-col items-center justify-center space-y-6 text-center">
                <Reveal trigger="onView" className="pb-2">
                  <h2 className="text-3xl md:text-4xl font-bold flex flex-col items-center gap-2">
                    <span className="text-black">Diagnosis</span>
                    <span className="text-purple text-2xl">↓</span>
                    <span className="text-black">Strategy</span>
                    <span className="text-purple text-2xl">↓</span>
                    <span className="text-black">Tactics</span>
                  </h2>
                </Reveal>
                <Reveal trigger="onView" delay={0.2} className="pb-2">
                  <p className="text-xl text-black/60 font-light">
                    We don't jump to execution. We earn it.
                  </p>
                </Reveal>
              </div>
              <Reveal trigger="onLoad" delay={0.3} width="full" className="!overflow-visible">
                <div className="relative flex items-center justify-center p-4 overflow-visible">
                  <img
                    src="/assets/images/how/dst-v2.png"
                    alt="Diagnosis Strategy Tactics Framework"
                    className="w-full md:w-[120%] md:max-w-none h-auto object-contain md:transform md:scale-110"
                    loading="eager"
                  />
                </div>
              </Reveal>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-stretch gap-6 md:gap-4">
              {/* Diagnosis */}
              <Reveal width="full" className="flex-1" trigger="onLoad">
                <div className="group bg-black/5 border border-black/5 rounded-lg p-8 lg:p-12 transition-all duration-300 hover:bg-[#c0ff09] hover:border-[#c0ff09] text-center flex flex-col items-center justify-center min-h-[350px] md:h-[450px] w-full">
                  <h3 className="text-2xl font-bold text-purple group-hover:text-black mb-8 transition-colors">Diagnosis</h3>
                  <div className="w-full flex-1 flex flex-col items-center justify-center space-y-6">
                    <span className="text-black group-hover:text-black font-light text-sm lg:text-base leading-snug transition-colors">Understand the real problem before acting</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Analyze market, audience, brand signals, and gaps</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Identify root causes, not surface symptoms</span>
                  </div>
                </div>
              </Reveal>

              <div className="flex items-center justify-center py-2 md:py-0">
                <ArrowRight className="text-purple hidden md:block" size={24} />
                <span className="text-purple md:hidden text-2xl font-bold">↓</span>
              </div>

              {/* Strategy */}
              <Reveal delay={0.1} width="full" className="flex-1" trigger="onLoad">
                <div className="group bg-black/5 border border-black/5 rounded-lg p-8 lg:p-12 transition-all duration-300 hover:bg-[#c0ff09] hover:border-[#c0ff09] text-center flex flex-col items-center justify-center min-h-[350px] md:h-[450px] w-full">
                  <h3 className="text-2xl font-bold text-purple group-hover:text-black mb-8 transition-colors">Strategy</h3>
                  <div className="w-full flex-1 flex flex-col items-center justify-center space-y-6">
                    <span className="text-black group-hover:text-black font-light text-sm lg:text-base leading-snug transition-colors">Decide the direction to solve the diagnosed problem</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Define clear objectives, positioning, and priorities</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Choose what to do and what not to do</span>
                  </div>
                </div>
              </Reveal>

              <div className="flex items-center justify-center py-2 md:py-0">
                <ArrowRight className="text-purple hidden md:block" size={24} />
                <span className="text-purple md:hidden text-2xl font-bold">↓</span>
              </div>

              {/* Tactics */}
              <Reveal delay={0.2} width="full" className="flex-1" trigger="onLoad">
                <div className="group bg-black/5 border border-black/5 rounded-lg p-8 lg:p-12 transition-all duration-300 hover:bg-[#c0ff09] hover:border-[#c0ff09] text-center flex flex-col items-center justify-center min-h-[350px] md:h-[450px] w-full">
                  <h3 className="text-2xl font-bold text-purple group-hover:text-black mb-8 transition-colors">Tactics</h3>
                  <div className="w-full flex-1 flex flex-col items-center justify-center space-y-6">
                    <span className="text-black group-hover:text-black font-light text-sm lg:text-base leading-snug transition-colors">Execute the strategy through specific actions</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Select tools, channels, content, and campaigns</span>
                    <svg className="w-16 h-2 text-purple/40 group-hover:text-black/40 transition-colors" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M2,8 Q50,0 98,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    <span className="text-black/60 group-hover:text-black/70 text-sm leading-snug transition-colors">Focus on how the strategy is implemented</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Science, Not Guesswork */}
      <section className="section-padding bg-black border-t border-white/10">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <Reveal className="mb-8">
              <h2 className="text-3xl font-bold text-white">Marketing Science, Not Guesswork</h2>
            </Reveal>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Byron Sharp - Left */}
              <div className="lg:col-span-4">
                <Reveal trigger="onLoad">
                  <div className="bg-black border border-white/10 rounded-lg overflow-hidden transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src="/images/thought-leaders/byron_sharp.jpg" alt="Byron Sharp" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="p-2 md:p-4 text-center bg-purple">
                      <p className="text-white text-sm md:text-base font-bold leading-tight">Byron Sharp</p>
                      <p className="text-[10px] md:text-xs text-white/80 leading-tight">Marketing Scientist & Professor</p>
                    </div>
                  </div>
                </Reveal>
              </div>

              {/* Text Content - Center */}
              <div className="col-span-2 lg:col-span-4 order-3 lg:order-none text-center">
                <Reveal delay={0.1}>
                  <p className="text-lg text-white/60 font-light mb-2">
                    Our work is informed by <span className="text-white font-bold">Byron Sharp</span> and <span className="text-white font-bold">How Brands Grow</span>, alongside <span className="text-white font-bold">Jenni Romaniuk</span>.
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-white/60 mb-4 max-w-2xl mx-auto">
                    Together, their research underscores the importance of mental and physical availability, distinctive brand assets, and consistent memory structures. Brands grow not by persuasion alone, but by being easy to recognise and recall across buying situations, making consistency and availability central to long-term growth.
                  </p>
                </Reveal>
                <Reveal delay={0.3}>
                  <div className="flex justify-center">
                    <p className="text-base max-w-2xl">
                      <span className="bg-white text-purple px-4 py-2 rounded inline-block font-bold">
                        We apply these principles with context, not dogma, adapting them to real-world Indian market conditions.
                      </span>
                    </p>
                  </div>
                </Reveal>
              </div>

              {/* Jenni Romaniuk - Right */}
              <div className="lg:col-span-4">
                <Reveal delay={0.2} trigger="onLoad">
                  <div className="bg-black border border-white/10 rounded-lg overflow-hidden transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src="/images/thought-leaders/jenni_romaniuk.png" alt="Jenni Romaniuk" className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="p-2 md:p-4 text-center bg-purple">
                      <p className="text-white text-sm md:text-base font-bold leading-tight">Jenni Romaniuk</p>
                      <p className="text-[10px] md:text-xs text-white/80 leading-tight">Marketing Scientist</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Thought Leaders Grid */}
      <section className="section-padding bg-black border-t border-white/10">
        <div className="container-wide">
          <div className="max-w-6xl mx-auto">
            <Reveal className="mb-4 text-center">
              <h2 className="text-3xl font-bold text-white">Inspired by Leading Thinkers</h2>
            </Reveal>
            <Reveal className="mb-12 text-center" delay={0.1}>
              <p className="text-xl text-white/60 max-w-3xl mx-auto">
                Shaped by Proven Frameworks. Anchored in Strategic Principles.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
              {[
                {
                  name: "Marty Neumeier",
                  role: "Brand Strategist & Author",
                  image: "/images/thought-leaders/marty_neumeier.png",
                  quote: "\"A brand is not what you say, it's what people believe.\"",
                  text: "We apply The Onlyness Test to ensure your brand stands alone in its category.",
                  footer: "Design, naming, and identity are tools to make strategy visible, memorable, and usable."
                },
                {
                  name: "Deborah MacInnis",
                  role: "Consumer Behaviour Professor",
                  image: "/images/thought-leaders/deborah_macinnis.png",
                  quote: "\"Brand admiration is the pinnacle of brand equity.\"",
                  text: "Her 3Es framework: Enabling, Enticing, and Enriching benefits.",
                  footer: "We use these filters to build brand-customer relationships that go beyond transactions."
                },
                {
                  name: "Rory Sutherland",
                  role: "Vice Chairman, Ogilvy",
                  image: "/images/thought-leaders/rory_sutherland.png",
                  quote: "\"The opposite of a good idea can also be a good idea.\"",
                  text: "Psychology over spreadsheets: warnings against mistaking efficiency for brand progress.",
                  footer: "We look for behavioral breakthroughs that logic and data alone often miss."
                },
                {
                  name: "Philip Kotler",
                  role: "Marketing Thought Leader",
                  image: "/images/thought-leaders/philip_kotler.png",
                  quote: "\"Marketing is not the art of finding clever ways to dispose of what you make.\"",
                  text: "The modern marketing shift: moving from selling to holistic value creation.",
                  footer: "We build customer-centric systems that focus on human value and long-term relationships."
                },
                {
                  name: "Michael Porter",
                  role: "Strategy Professor & Economist",
                  image: "/images/thought-leaders/michael_porter.png",
                  quote: "\"The essence of strategy is choosing what not to do.\"",
                  text: "Competitive Strategy: understanding the industry forces that shape real advantage.",
                  footer: "We apply his frameworks to find a sustainable, unique position in your market."
                },
                {
                  name: "Meghan Busse",
                  role: "Professor of Strategy",
                  image: "/images/thought-leaders/meghan_busse.png",
                  quote: "\"Strategy is about making choices that allow a firm to create and capture value.\"",
                  text: "Firm Strategy & Economics: using economic logic to understand competitive discipline and value-based choices.",
                  footer: "We apply her framework to move beyond aspirations and focus on distinct economic advantage."
                },
                {
                  name: "Robert Greene",
                  role: "Strategist & Author",
                  image: "/images/thought-leaders/robert_greene.jpg",
                  quote: "\"The future belongs to those who learn more skills and combine them in creative ways.\"",
                  text: "On strategic depth and advantage, winning by shaping the game itself, not just reacting.",
                  footer: "We help brands build the strategic discipline needed to lead their category."
                }
              ].map((leader, i) => (
                <Reveal key={i} delay={i * 0.1} trigger="onLoad" width="full" className="h-full">
                  <div className="bg-white/5 border border-white/5 rounded-lg overflow-hidden flex flex-col h-full transition-all duration-300 hover:border-[#c0ff09]/50">
                    <img src={leader.image} alt={leader.name} className="w-full aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-500" loading="eager" />
                    <div className="bg-purple p-2 md:p-4 text-center">
                      <p className="text-white text-sm md:text-base font-bold leading-tight">{leader.name}</p>
                      <p className="text-white/80 text-[10px] md:text-xs leading-tight">{leader.role}</p>
                    </div>
                    <div className="p-3 md:p-6 flex-1 flex flex-col">
                      <p className="text-[10px] md:text-sm text-white/50 italic mb-2 md:mb-4 leading-snug md:leading-relaxed">
                        {leader.quote}
                      </p>
                      <p className="text-[10px] md:text-sm text-white/70 mb-2 md:mb-4 leading-snug md:leading-relaxed">
                        {leader.text}
                      </p>
                      <p className="text-[10px] md:text-sm text-white/50 mt-auto leading-snug md:leading-relaxed">
                        {leader.footer}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding violet-glow-section">
        <div className="container-narrow text-center relative z-10">
          <Reveal className="mx-auto mb-8 pb-2">
            <h2 className="display-md text-white">
              Ready to build with strategy, not guesswork?
            </h2>
          </Reveal>
          <Reveal className="mx-auto" delay={0.2}>
            <Button className="bg-white text-black hover:bg-black hover:text-black rounded-full px-8 py-6 text-lg font-bold transition-all duration-300 border-none" asChild>
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default HowPage;
